import { useMemo, useState, useEffect, useRef } from "react";
import Header from "../components/HeaderV2";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/NoticeV2.module.css";
import ErrMsg from "../ErrMsg";
import { SelectorInput } from "../components/Inputs";
import { NoticeType, useNotice, useNoticeMutations } from "../NoticeContext";
import { usePartner } from "../PartnerContext";
import Editor from "../components/WYSIWYG";

type EditorAsset = { tempUrl: string; file: File; type: "img" | "video" };

const Notice = () => {
    const { partner } = usePartner();
    const { notices, selectedPartnerId, setSelectedPartnerId } = useNotice();
    const [addNotice, setAddNotice] = useState<boolean>(false);
    const [detailNotice, setDetailNotice] = useState<NoticeType | null>(null);

    const selectedPartner = useMemo(
        () => partner.find((p) => p.id === selectedPartnerId),
        [partner, selectedPartnerId]
    );

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>공지사항 관리</h4>
                    <p className={common["text--copy"]}>파트너사별 공지사항을 등록하고 내용을 확인합니다.</p>

                    <section className={style["control-panel"]}>
                        <div className={style["panel-header"]}>
                            <div>
                                <p className={style["section-kicker"]}>게시 대상</p>
                                <h5 className={style["panel-title"]}>
                                    {selectedPartner ? selectedPartner.name : "파트너사를 선택하세요"}
                                </h5>
                                <p className={style["panel-copy"]}>
                                    {selectedPartner
                                        ? `${selectedPartner.id} · 등록된 공지 ${notices.length}개`
                                        : "공지사항을 관리할 파트너사를 먼저 선택해야 합니다."}
                                </p>
                            </div>
                            <button
                                className={style["button--primary"]}
                                onClick={() => setAddNotice(true)}
                                disabled={!selectedPartnerId}
                            >
                                공지사항 등록
                            </button>
                        </div>
                        <div className={style["selector-wrap"]}>
                            <SelectorInput
                                placeholder="파트너사 선택"
                                value={selectedPartnerId || ""}
                                setValue={setSelectedPartnerId}
                            >
                                {partner.map((p) => (
                                    <option key={p.docId} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </SelectorInput>
                        </div>
                    </section>

                    {selectedPartnerId ? (
                        <NoticeTable onOpenDetail={(notice) => setDetailNotice(notice)} />
                    ) : (
                        <div className={style["empty-state"]}>파트너사를 선택하면 공지사항 목록이 표시됩니다.</div>
                    )}
                </div>
            </div>

            {addNotice && selectedPartnerId && (
                <AddNotice
                    partnerName={selectedPartner?.name || selectedPartnerId}
                    partnerId={selectedPartnerId}
                    close={() => setAddNotice(false)}
                    onSaved={() => {
                        setAddNotice(false);
                    }}
                />
            )}

            {detailNotice && <NoticeDetailPopup notice={detailNotice} close={() => setDetailNotice(null)} />}
        </>
    );
};

const NoticeTable = ({ onOpenDetail }: { onOpenDetail: (notice: NoticeType) => void }) => {
    const { notices } = useNotice();
    const [search, setSearch] = useState<string>("");

    const filteredNotices = notices.filter((notice) => {
        const keyword = search.toLowerCase();
        return search === "" || notice.title.toLowerCase().includes(keyword);
    });

    return (
        <section className={style["notice-panel"]}>
            <div className={style["panel-header"]}>
                <div>
                    <p className={style["section-kicker"]}>공지사항 목록</p>
                    <h5 className={style["panel-title"]}>등록된 공지사항</h5>
                    <p className={style["panel-copy"]}>총 {notices.length}개 중 {filteredNotices.length}개 표시</p>
                </div>
            </div>
            <div className={style["toolbar"]}>
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="공지 제목으로 검색"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <div className={style["wrapper--notice-list"]}>
                <div className={[style["notice-row"], style["notice-row--head"]].join(" ")}>
                    <p>제목</p>
                    <p>작성일</p>
                    <p>관리</p>
                </div>
                {filteredNotices.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                {filteredNotices.map((notice) => (
                    <NoticeRow key={notice.id} notice={notice} onOpen={() => onOpenDetail(notice)} />
                ))}
            </div>
        </section>
    );
};

const NoticeRow = ({ notice, onOpen }: { notice: NoticeType; onOpen: () => void }) => (
    <div className={style["unit--notice-list"]} onClick={onOpen}>
        <div className={style["notice-row"]}>
            <p className={style["notice-title"]}>{notice.title}</p>
            <p className={style["notice-date"]}>{formatDateTime(notice.timestamp)}</p>
            <div className={style["row-actions"]}>
                <button className={style["button--ghost"]}>상세</button>
            </div>
        </div>
    </div>
);

const AddNotice = ({
    partnerName,
    partnerId,
    close,
    onSaved,
}: {
    partnerName: string;
    partnerId: string;
    close: () => void;
    onSaved?: () => void;
}) => {
    const { createNotice } = useNoticeMutations();
    const [title, setTitle] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [editorAssets, setEditorAssets] = useState<EditorAsset[]>([]);
    const [active, setActive] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(true);

    useEffect(() => {
        setActive(true);
        return () => setIsMounted(false);
    }, []);

    const hide = () => {
        if ((title.trim() || content.trim()) && !window.confirm("작성 중인 내용을 닫으시겠습니까?")) return;

        setActive(false);
        setTimeout(() => {
            if (isMounted) close();
        }, 520);
    };

    const saveNotice = async () => {
        if (saving || !isMounted) return;

        setSaving(true);
        try {
            await createNotice(title, content, editorAssets);
            if (isMounted) {
                onSaved?.();
            }
        } catch (err: any) {
            console.error(err);
            if (isMounted) {
                alert(err.message || "저장 중 에러가 발생했습니다.");
            }
        } finally {
            if (isMounted) setSaving(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>공지사항 등록</p>
                        <h4 className={style["modal-title"]}>새 공지 작성</h4>
                        <p className={style["modal-copy"]}>{partnerName}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>게시 대상</h5>
                            <p>선택된 파트너사에만 공지사항이 게시됩니다.</p>
                        </div>
                        <div className={style["target-card"]}>
                            <span>{partnerId}</span>
                            <strong>{partnerName}</strong>
                        </div>
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>공지 정보 입력</h5>
                            <p>제목과 내용을 입력하고 이미지 또는 영상을 첨부할 수 있습니다.</p>
                        </div>
                        <div className={style["editor-shell"]}>
                            <Editor
                                initialTitle={title}
                                initialContent={content}
                                onTitleChange={setTitle}
                                onChange={setContent}
                                onAssetsChange={setEditorAssets}
                            />
                        </div>
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={saveNotice}>
                        {saving ? "등록 중" : "등록하기"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const NoticeDetailPopup = ({ notice, close }: { notice: NoticeType; close: () => void }) => {
    const { deleteNoticeWithConfirm, sanitizeHtml } = useNoticeMutations();
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setActive(true);
    }, []);

    useEffect(() => {
        if (!active) return;
        const root = contentRef.current;
        if (!root) return;

        const videos = Array.from(root.querySelectorAll("video")) as HTMLVideoElement[];
        videos.forEach((v) => {
            if (!v.hasAttribute("controls")) v.setAttribute("controls", "true");
            if (!v.hasAttribute("playsinline")) v.setAttribute("playsinline", "true");
            if (!v.hasAttribute("webkit-playsinline")) v.setAttribute("webkit-playsinline", "true");
            if (v.style.pointerEvents === "none") v.style.pointerEvents = "auto";
        });
    }, [active, notice.content]);

    const handleClose = () => {
        setActive(false);
        setTimeout(() => close(), 520);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            const result = await deleteNoticeWithConfirm(notice.id);
            if (result && result.success) {
                handleClose();
            }
        } catch (err: any) {
            console.error(err);
            if (ErrMsg[err?.message]) {
                alert(ErrMsg[err.message]);
            } else {
                alert(err.message || "삭제 중 에러가 발생했습니다.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div
                className={[
                    common["unit--overlay"],
                    style["modal"],
                    style["modal--wide"],
                    active && common["unit--overlay--active"],
                ].join(" ")}
            >
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>공지사항 상세</p>
                        <h4 className={style["modal-title"]}>{notice.title}</h4>
                        <p className={style["modal-copy"]}>{formatDateTime(notice.timestamp)}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={handleClose} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div
                            ref={contentRef}
                            className={style["container--notice"]}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.content) }}
                        />
                    </div>

                    <div className={[style["modal-section"], style["danger-zone"]].join(" ")}>
                        <div className={style["section-head"]}>
                            <h5>공지사항 삭제</h5>
                            <p>삭제한 공지사항은 목록에서 제거되며 첨부 파일도 함께 정리됩니다.</p>
                        </div>
                        <DeleteNoticeButton loading={loading} onClick={handleDelete} />
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={handleClose}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

const DeleteNoticeButton = ({ loading, onClick }: { loading: boolean; onClick: () => void }) => {
    const [confirming, setConfirming] = useState<boolean>(false);
    const buttonRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!confirming) return;

        const close = (event: MouseEvent) => {
            if (!buttonRef.current?.contains(event.target as Node)) {
                setConfirming(false);
            }
        };

        document.body.addEventListener("click", close);
        return () => document.body.removeEventListener("click", close);
    }, [confirming]);

    return (
        <div ref={buttonRef}>
            <button
                type="button"
                className={[style["button--danger"], confirming && style["button--danger-confirm"]].filter(Boolean).join(" ")}
                disabled={loading}
                onClick={() => {
                    if (!confirming) {
                        setConfirming(true);
                        return;
                    }
                    onClick();
                    setConfirming(false);
                }}
            >
                {loading ? "삭제 중" : confirming ? "삭제 승인하기" : "삭제 처리하기"}
            </button>
        </div>
    );
};

const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return "-";
    return new Date(Number(timestamp)).toLocaleString("ko-KR");
};

export default Notice;
